import React, { useEffect, useRef, useState } from "react";
import { Calendar, Check, ChevronDown } from "lucide-react";

// Shared date control: one split-look button (basis │ range ⌄) with one menu.
// Used by Encounter review (DateRangePicker wraps it) and Claims Queue. The divider is visual only.
const HAIR = "#E9E9EC", INK = "#141412", SUB = "#6C6C69", FAINT = "#9B9B97";
const POP = { background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, boxShadow: "0 10px 28px rgba(20,20,18,.14)" };

const BASIS_ROWS = [["added", "Date added", "When it reached revQ"], ["dos", "Date of service", "When the visit happened"]];

// The two labelled groups (SHOW … BY + RANGE). Reused inside the overflow (⋯) menu too.
export function DateBasisSections({ basis, onBasisChange, noun = "visits", presets, rangeKey, onPickRange, custom, prefix }) {
  return (
    <>
      <div className="text-[11px] font-extrabold uppercase tracking-[.06em] px-2 pt-1 pb-1" style={{ color: FAINT }}>Show {noun} by</div>
      {BASIS_ROWS.map(([k, t, sub]) => {
        const on = basis === k;
        return (
          <button key={k} type="button" onClick={() => onBasisChange?.(k)} className="w-full flex items-start text-left px-2.5 py-2 rounded-[7px] hover:bg-[#F1F1F4]" data-testid={`${prefix}-basis-${k}`}>
            <span className="w-[18px] flex-none mt-[2px]">{on ? <Check size={13} /> : null}</span>
            <span><span className="block text-[13px]" style={{ color: INK, fontWeight: on ? 600 : 500 }}>{t}</span><span className="block text-[11.5px]" style={{ color: FAINT }}>{sub}</span></span>
          </button>
        );
      })}
      <div style={{ height: 1, background: HAIR, margin: "6px 4px" }} />
      <div className="text-[11px] font-extrabold uppercase tracking-[.06em] px-2 pt-1 pb-1" style={{ color: FAINT }}>Range</div>
      {presets.map(([k, t]) => (
        <button key={k} type="button" onClick={() => onPickRange(k)} className="w-full flex items-center text-left px-2.5 py-2 rounded-[7px] text-[13px] hover:bg-[#F1F1F4]" style={{ color: INK }} data-testid={`${prefix}-${k}`}>
          <span className="w-[18px] flex-none">{rangeKey === k ? <Check size={13} /> : null}</span>{t}
        </button>
      ))}
      {custom ? (
        <>
          <div style={{ height: 1, background: HAIR, margin: "6px 4px" }} />
          <button type="button" onClick={custom.onOpen} className="w-full flex items-center text-left px-2.5 py-2 rounded-[7px] text-[13px] hover:bg-[#F1F1F4]" style={{ color: INK }} data-testid={`${prefix}-custom`}>
            <span className="w-[18px] flex-none">{custom.selected ? <Check size={13} /> : null}</span>Custom range…
          </button>
        </>
      ) : null}
    </>
  );
}

export function DateBasisButton({ basis, onBasisChange, noun = "visits", rangeLabel, presets, rangeKey, onPickRange, custom, addedTip, dosTip, disabled, prefix = "date", inGroup }) {
  const [mode, setMode] = useState(null); // null | "menu" | "cal"
  const [hover, setHover] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!mode) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setMode(null); };
    const onKey = (e) => { if (e.key === "Escape") setMode(null); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [mode]);
  const basisLabel = basis === "dos" ? "Service" : "Added";
  const lit = hover || mode === "menu";
  const pickRange = (k) => { onPickRange(k); setMode(null); };
  return (
    <div className="relative flex-none" ref={ref}>
      <button type="button" onClick={() => setMode((m) => (m ? null : "menu"))} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        aria-expanded={mode === "menu"} aria-label={`${basis === "dos" ? "Date of service" : "Date added"}, ${rangeLabel}`} title={basis === "dos" ? dosTip : addedTip}
        className="inline-flex items-center gap-[7px] text-[12.5px] whitespace-nowrap" style={{ height: inGroup ? 36 : 38, padding: "0 12px", border: inGroup ? "none" : `1px solid ${lit ? "#D9D9DE" : HAIR}`, borderRadius: inGroup ? 0 : 10, background: lit && !inGroup ? "#F1F1F4" : (inGroup && lit ? "#F1F1F4" : "#fff"), opacity: disabled ? 0.45 : 1 }} data-testid={`${prefix}-btn`}>
        <span style={{ color: FAINT, fontWeight: 500 }} data-testid={`${prefix}-basis`}>{basisLabel}</span>
        <span style={{ color: INK, fontWeight: 600 }} data-testid={`${prefix}-label`}>{rangeLabel}</span>
        <ChevronDown size={11} style={{ color: FAINT }} />
      </button>
      {mode === "menu" ? (
        <div className="absolute right-0 z-40 mt-1 p-1.5 min-w-[260px]" style={{ ...POP, maxWidth: "calc(100vw - 24px)" }} data-testid={`${prefix}-menu`}>
          <DateBasisSections basis={basis} onBasisChange={onBasisChange} noun={noun} presets={presets} rangeKey={rangeKey} onPickRange={pickRange}
            custom={custom ? { selected: custom.selected, onOpen: () => setMode("cal") } : null} prefix={prefix} />
        </div>
      ) : null}
      {mode === "cal" && custom ? custom.render(() => setMode(null)) : null}
    </div>
  );
}
